import { useEffect, useMemo, useState } from "react";
import type { Layout } from "react-grid-layout/legacy";
import { pageHeightPx, gridHeightPx, GUTTER_OFFSET_PX } from "../PDF/pdfLayout";

interface PageBreakOverlayProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  layout: Layout;
}

/**
 * Renders horizontal dashed page-break guide lines over the grid.
 * Each line marks where one A4 landscape page ends and the next begins.
 * Also sets minHeight on the container so the full last occupied page is visible.
 */
export function PageBreakOverlay({ containerRef, layout }: PageBreakOverlayProps) {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  const { breakLines, minHeight } = useMemo(() => {
    if (containerWidth === 0 || layout.length === 0)
      return { breakLines: [] as { y: number; pageNum: number }[], minHeight: 0 };

    const phPx = pageHeightPx(containerWidth);
    const totalHeight = gridHeightPx(layout, containerWidth);
    const lines: { y: number; pageNum: number }[] = [];

    // Start from the first page break (end of page 1)
    let y = phPx;
    let page = 1;
    while (y < totalHeight) {
      lines.push({ y, pageNum: page });
      page++;
      y += phPx;
    }

    // If content meaningfully overflows past the last page break, show the full next page.
    // Use a small threshold (half a grid row) to ignore sub-pixel / boundary-touching cases.
    const lastBreak = lines.length > 0 ? lines[lines.length - 1].y : 0;
    const overflow = totalHeight - lastBreak;
    const mh = lastBreak > 0 && overflow > 15 ? lastBreak + phPx : 0;

    return { breakLines: lines, minHeight: mh };
  }, [containerWidth, layout]);

  // Apply minHeight directly on the grid container so the full last page is visible
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.minHeight = minHeight > 0 ? `${minHeight}px` : "";
  }, [containerRef, minHeight]);

  if (breakLines.length === 0) return null;

  return (
    <>
      {/* Horizontal page-break guides */}
      {breakLines.map(({ y, pageNum }) => (
        <div
          key={pageNum}
          className="page-break-guide"
          style={{ top: y + GUTTER_OFFSET_PX }}
        >
          <span className="page-break-label">Page {pageNum} / {pageNum + 1}</span>
        </div>
      ))}
    </>
  );
}
